<template>
  <v-card class="main-card">
    <v-card-title class="screen-title">
      <span>ユーザ一覧</span>
      <manual-button path="/user-list/" />
    </v-card-title>
    <v-card-text class="pa-2" v-if="isLoaded">
      <v-row no-gutters>
        <v-col>
          <app-search :search="searchOption" v-model="table.filter" />
        </v-col>
        <div class="d-flex flex-row ga-1 flex-wrap ml-2">
          <v-btn color="primary" v-permission.config @click="showUserRegister"
            >追加</v-btn
          >
          <v-btn color="primary" v-permission.config @click="del">削除</v-btn>
          <v-btn color="primary" @click="downloadCsv">一覧ダウンロード</v-btn>
        </div>
      </v-row>
      <v-alert v-if="alertMsg" class="my-2" :text="alertMsg"></v-alert>
      <div class="d-flex flex-row justify-end mb-2">
        <v-btn color="primary" @click="load">一覧更新</v-btn>
      </div>
      <app-table
        v-model:table-sort-by="table.sortBy"
        primary-key="userSeq"
        :selectable="selectable"
        :fields="table.fields"
        :items="users.userList"
        :filterOption="table.filter_option"
        :filter="table.filter"
        :sort-compare="customSortUser"
        v-model="table.selected"
        :hover="true"
        sticky-header="50vh"
        @click:row="userDetails"
      >
        <template #cell(NameKana)="{ value }">
          {{ $filter.emptyTo(value, "-") }}
        </template>
        <template #cell(group)="{ value }">
          {{ $filter.emptyTo(value, "-") }}
        </template>
        <template #cell(phone)="{ value }">
          {{ $filter.emptyTo(value, "-") }}
        </template>
      </app-table>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { mapState } from "vuex";
import type { UserEntity, UserGetEntity } from "@/apis/UserApi";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { SearchOption } from "@/components/AppSearch.vue";
import sortCompare from "@/services/SortService";
import UserReference from "@/modals/user/UserReference.vue";
import UserEdit from "@/modals/user/UserEdit.vue";

export default {
  name: "UserList",
  data() {
    return {
      table: {
        filter: {} as Record<string, unknown>,
        filter_option: {
          loginId: "like",
          Name: "like",
          group: "equal",
          email: "like",
          operationType: "equal",
        } as FilterOption,
        sortBy: { key: "loginId", order: "asc" } as SortItem,
        fields: [
          {
            key: "loginId",
            title: "ログインID",
            sortable: true,
            width: "200px",
          },
          {
            key: "Name",
            title: "氏名",
            sortable: true,
            width: "170px",
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (_v: string, _k: null, item: UserEntity) => {
              return `${item.familyName} ${item.givenName}`;
            },
          },
          {
            key: "NameKana",
            title: "氏名(カナ)",
            sortable: true,
            sortByFormatted: true,
            width: "200px",
            filterByFormatted: true,
            formatter: (_v: string, _k: null, item: UserEntity) => {
              if (item.familyNameKana === null && item.givenNameKana === null) {
                return null;
              } else if (item.familyNameKana === null) {
                return `${item.givenNameKana}`;
              } else if (item.givenNameKana === null) {
                return `${item.familyNameKana}`;
              } else {
                return `${item.familyNameKana} ${item.givenNameKana}`;
              }
            },
          },
          {
            key: "group",
            title: "所属グループ",
            sortable: true,
            width: "200px",
          },
          {
            key: "phone",
            title: "連絡先電話番号",
            sortable: true,
            width: "170px",
          },
          {
            key: "email",
            title: "連絡先メールアドレス",
            sortable: true,
            width: "250px",
          },
          {
            key: "operationType",
            title: "運用種別",
            sortable: true,
            sortByFormatted: true,
            width: "170px",
            filterByFormatted: false,
            formatter: (_v: string) => this.$filter.enumTo(_v, "operationType"),
          },
        ] as AppTableHeader<UserEntity>[],
        selected: [] as UserEntity[],
      },

      alertMsg: null as string | null,
      users: {} as UserGetEntity,
      isLoaded: false,
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  computed: {
    ...mapState("user", {
      loginUser: "loginUser",
    }),
    selectable(): boolean {
      return this.$service.permission.hasAuthority("CONFIG");
    },
    /** 検索コンポーネントのオプション */
    searchOption(): SearchOption {
      return {
        loginId: { type: "text", label: "ログインID" },
        Name: { type: "text", label: "氏名" },
        group: {
          type: "select",
          label: "所属グループ",
          // 必要の場合のみ選択肢の最後に-を追加
          items: this.users.userList
            .map((e) => e.group)
            .filter((e) => e)
            .uniq()
            .sortBy("asc")
            .map<{
              text: string;
              value: string | null;
            }>((e) => ({ text: e!, value: e! }))
            .concat(
              this.users.userList.find((e) => !e.group)
                ? [{ text: "-", value: null }]
                : []
            ),
        },
        email: { type: "text", label: "連絡先メールアドレス" },
        operationType: {
          type: "select",
          label: "運用種別",
          items: [
            { text: "法人管理者", value: "ADMIN" },
            { text: "設定権限", value: "SETTING" },
            { text: "閲覧権限", value: "VIEWING" },
          ],
        },
      };
    },
  },
  methods: {
    /** データ取得 */
    /** 法人権限の場合、APIより自分以下の権限のユーザのみ返却される */
    async load() {
      this.alertMsg = null;
      this.users = await this.$api.user.getAll();
      // 認証IPアドレスの初期ソート対応
      this.users.userList.forEach((e) => {
        e.ipAuthAddressList.sort();
      });
    },
    /** 第二優先を必ずログインIDの昇順にするソート */
    customSortUser(a: UserEntity, b: UserEntity): number {
      const formatterByField = this.table.fields.find((e) => {
        return (
          e.key === this.table.sortBy.key && e.sortByFormatted && e.formatter
        );
      });
      return sortCompare(a, b, [
        [
          formatterByField
            ? (e: UserEntity) => {
                return (formatterByField.formatter as Function)(
                  e[this.table.sortBy.key as keyof UserEntity],
                  this.table.sortBy.key,
                  e
                );
              }
            : (this.table.sortBy.key as keyof UserEntity),
          "asc",
        ],
        [(e) => e.loginId, this.table.sortBy.order],
      ]);
    },
    /** 追加 */
    async showUserRegister() {
      this.alertMsg = null;
      await this.$modal.show(UserEdit, {
        vnumber: this.users.vnumber,
      });
      await this.load();
    },
    /** 参照へ */
    async userDetails(item: UserEntity) {
      this.alertMsg = null;
      await this.$modal.show(UserReference, {
        user: item,
        userKeyId: this.users.userKeyId,
        loginUser: this.loginUser,
      });
      await this.load();
    },
    /** 削除 */
    async del() {
      if (this.table.selected.length > 0) {
        this.alertMsg = null;
        await this.$confirm(`ユーザを削除します。よろしいですか？`);
        await this.$api.user.delete({
          userSeqList: this.table.selected.map((e) => e.userSeq),
          userKeyId: this.users.userKeyId,
        });
        await this.load();
      } else {
        this.alertMsg = this.$filter.message("no_select_data");
      }
    },
    /** フロント側のみでのCSVダウンロード */
    downloadCsv() {
      this.$service.csvService.appTableCsvDownLoad(
        "user",
        this.users.userList.sortBy(["loginId", "asc"]),
        this.$service.csvService.genFieldList(this.table.fields)
      );
    },
  },
};
</script>

<style></style>
